import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  ArrowRight,
  Building2,
  ChartColumn,
  CheckCircle2,
  CircleHelp,
  FileBarChart2,
  FileText,
  HandCoins,
  LayoutDashboard,
  Receipt,
  PhoneCall,
  ShieldCheck,
  Users,
  Wrench
} from 'lucide-react'
import LandingMainHeader from './LandingMainHeader'
import {
  getOrCreateAnonymousId,
  getStoredSessionId,
  registerLandingEvent,
  registerLandingVisit,
  setStoredSessionId
} from '../../services/landingTracking'

const TURNSTILE_SITE_KEY = (() => {
  if (import.meta.env.VITE_TURNSTILE_SITE_KEY) {
    return import.meta.env.VITE_TURNSTILE_SITE_KEY
  }

  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '0x4AAAAAACewzlyKjy6eQokF'
  }

  return '0x4AAAAAACewdq3FV9qGlLZJ'
})()

const countries = [
  'Argentina',
  'Bolivia',
  'Brasil',
  'Chile',
  'Colombia',
  'Costa Rica',
  'Ecuador',
  'El Salvador',
  'Guatemala',
  'Honduras',
  'Mexico',
  'Nicaragua',
  'Panama',
  'Paraguay',
  'Peru',
  'Republica Dominicana',
  'Uruguay',
  'Venezuela',
  'Otro'
]

const roleOptions = [
  { label: 'Propietario', value: 'Propietario' },
  { label: 'Administrador', value: 'Administrador' },
  { label: 'Inmobiliaria', value: 'Inmobiliaria' },
  { label: 'Otro', value: 'Otro' }
]

const companySizes = [
  { label: '1-5 empleados', value: '1-5' },
  { label: '6-20 empleados', value: '6-20' },
  { label: '21-50 empleados', value: '21-50' },
  { label: '50+ empleados', value: '50+' }
]

const interests = [
  { label: 'Demo', value: 'Demo' },
  { label: 'Cotizacion', value: 'Cotizacion' },
  { label: 'Consultoria', value: 'Consultoria' }
]

const faqs = [
  {
    q: 'Cuanto tiempo tarda implementar RentalBiz?',
    a: 'Podemos arrancar una operacion base en pocas semanas priorizando inmuebles, contratos, cobros y cartera.'
  },
  {
    q: 'RentalBiz sirve para inmuebles residenciales y comerciales?',
    a: 'Si. Puedes administrar propiedades residenciales, comerciales o mixtas desde la misma plataforma.'
  },
  {
    q: 'Como ayuda a reducir la mora?',
    a: 'Centraliza vencimientos, recordatorios, estado de cartera y seguimiento de pagos para actuar antes del atraso.'
  },
  {
    q: 'Puedo controlar mantenimiento y solicitudes?',
    a: 'Si. RentalBiz registra solicitudes, tareas y estados de mantenimiento por inmueble con trazabilidad.'
  },
  {
    q: 'Puedo pedir demo o cotizacion sin compromiso?',
    a: 'Si. Agenda una demo o cotizacion y evaluamos contigo el escenario real de tu operacion.'
  }
]

const initialForm = {
  fullName: '',
  company: '',
  role: 'Administrador',
  email: '',
  phone: '',
  country: 'Colombia',
  companySize: '6-20',
  interest: 'Demo',
  consentAccepted: false,
  website: ''
}

const toCanonicalSlug = (region) => `/${region}/soluciones/alquiler-inmuebles/rentalbiz`

const parseUtm = (search) => {
  const params = new URLSearchParams(search)
  return {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
    utmTerm: params.get('utm_term') || '',
    utmContent: params.get('utm_content') || ''
  }
}

const upsertMeta = (attributeName, key, content) => {
  if (!content) return

  const selector = `meta[${attributeName}="${key}"]`
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attributeName, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

const upsertCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', href)
}

const upsertJsonLd = (json) => {
  const id = 'rentalbiz-jsonld'
  let script = document.getElementById(id)
  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(json)
}

const safeEventPayload = (value) => {
  try {
    return JSON.stringify(value)
  } catch {
    return '{}'
  }
}

export default function RentalBizLanding() {
  const { region, category, product } = useParams()
  const location = useLocation()
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({})
  const [submitState, setSubmitState] = useState({ type: '', message: '' })
  const [sending, setSending] = useState(false)
  const [testCampaignState, setTestCampaignState] = useState({ type: '', message: '' })
  const [sendingTestCampaign, setSendingTestCampaign] = useState(false)
  const [smtpTestState, setSmtpTestState] = useState({ type: '', message: '' })
  const [sendingSmtpTest, setSendingSmtpTest] = useState(false)
  const [sessionId, setSessionId] = useState(() => getStoredSessionId())
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileContainerRef = useRef(null)
  const turnstileWidgetIdRef = useRef(null)
  const formStartedRef = useRef(false)
  const featuresSectionRef = useRef(null)
  const featureScrollTrackedRef = useRef(false)

  const normalizedRegion = (region || 'latam').toLowerCase()
  const normalizedCategory = (category || 'alquiler-inmuebles').toLowerCase()
  const normalizedProduct = (product || 'rentalbiz').toLowerCase()
  const isValidRoute = normalizedCategory === 'alquiler-inmuebles' && normalizedProduct === 'rentalbiz'
  const landingSlug = toCanonicalSlug(normalizedRegion)

  const utm = useMemo(() => parseUtm(location.search), [location.search])

  const productCode = 'RENTALBIZ'

  const whatsappNumber = (import.meta.env.VITE_RENTALBIZ_WHATSAPP_NUMBER || '573188474508').replace(/[^0-9]/g, '')
  const whatsappText = import.meta.env.VITE_RENTALBIZ_WHATSAPP_TEXT || 'Hola, quiero informacion de RentalBiz para mi operacion inmobiliaria.'
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappText)}`
  const demoEntryUrl = '/api/auth/demo-entry?product=rentalbiz'
  const showBrevoTestButton = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  useEffect(() => {
    if (!isValidRoute) return

    const canonicalUrl = `${window.location.origin}${landingSlug}`
    const title = 'RentalBiz LATAM | AppEmprendedor'
    const description = 'RentalBiz de AppEmprendedor centraliza inmuebles, inquilinos, contratos, cobros, cartera y mantenimiento para mejorar ocupacion y reducir mora.'

    document.title = title
    upsertCanonical(canonicalUrl)
    upsertMeta('name', 'description', description)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)

    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      applicationCategory: 'BusinessApplication',
      name: 'RentalBiz',
      description,
      operatingSystem: 'Web',
      brand: {
        '@type': 'Brand',
        name: 'AppEmprendedor'
      },
      areaServed: normalizedRegion.toUpperCase(),
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Demo inicial'
      }
    })
  }, [isValidRoute, landingSlug, normalizedRegion])

  useEffect(() => {
    if (!isValidRoute || !TURNSTILE_SITE_KEY) {
      return undefined
    }

    let disposed = false

    const renderWidget = () => {
      if (disposed || !turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current !== null) {
        return
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken('')
      })
    }

    if (window.turnstile) {
      renderWidget()
      return () => {
        disposed = true
      }
    }

    let script = document.querySelector('script[data-turnstile="true"]')
    if (!script) {
      script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.setAttribute('data-turnstile', 'true')
      document.head.appendChild(script)
    }

    script.addEventListener('load', renderWidget)

    return () => {
      disposed = true
      script.removeEventListener('load', renderWidget)
    }
  }, [isValidRoute])

  useEffect(() => {
    if (!isValidRoute) return

    const currentSessionId = getStoredSessionId()
    const anonymousId = getOrCreateAnonymousId()

    const run = async () => {
      try {
        const visitResult = await registerLandingVisit({
          landingSlug,
          productCode,
          sessionId: currentSessionId || undefined,
          anonymousId,
          referrer: document.referrer || undefined,
          ...utm
        })

        if (visitResult?.sessionId) {
          setStoredSessionId(visitResult.sessionId)
          setSessionId(visitResult.sessionId)
        }

        await registerLandingEvent({
          landingSlug,
          productCode,
          eventName: 'page_view',
          sessionId: visitResult?.sessionId || currentSessionId || 'anonymous',
          payloadJson: safeEventPayload({ path: location.pathname }),
          ...utm
        })
      } catch (error) {
        console.error('No se pudo registrar tracking inicial de landing', error)
      }
    }

    run()
  }, [isValidRoute, landingSlug, location.pathname, productCode, utm])

  const trackEvent = useCallback(async (eventName, payload = {}) => {
    try {
      await registerLandingEvent({
        landingSlug,
        productCode,
        eventName,
        sessionId: sessionId || getStoredSessionId() || 'anonymous',
        payloadJson: safeEventPayload(payload),
        ...utm
      })
    } catch (error) {
      console.error(`Error registrando evento ${eventName}`, error)
    }
  }, [landingSlug, productCode, sessionId, utm])

  useEffect(() => {
    if (!isValidRoute || !featuresSectionRef.current || featureScrollTrackedRef.current) {
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const hasVisibleEntry = entries.some((entry) => entry.isIntersecting)
        if (hasVisibleEntry && !featureScrollTrackedRef.current) {
          featureScrollTrackedRef.current = true
          trackEvent('feature_scroll', { section: 'funcionalidades' })
          observer.disconnect()
        }
      },
      {
        threshold: 0.45
      }
    )

    observer.observe(featuresSectionRef.current)
    return () => observer.disconnect()
  }, [isValidRoute, trackEvent])

  const startFormTracking = () => {
    if (formStartedRef.current) return
    formStartedRef.current = true
    trackEvent('form_start', { source: 'rentalbiz_form' })
  }

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validateForm = () => {
    const nextErrors = {}

    if (!form.fullName.trim()) nextErrors.fullName = 'Ingresa tu nombre completo.'
    if (!form.company.trim()) nextErrors.company = 'Ingresa empresa o propietario.'
    if (!form.role.trim()) nextErrors.role = 'Selecciona un rol.'
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = 'Ingresa un email valido.'
    }
    if (!form.phone.trim() || !/^\+?[0-9\s\-()]{8,20}$/.test(form.phone.trim())) {
      nextErrors.phone = 'Ingresa un telefono valido LATAM.'
    }
    if (!form.country.trim()) nextErrors.country = 'Selecciona un pais.'
    if (!form.companySize) nextErrors.companySize = 'Selecciona cantidad de inmuebles.'
    if (!form.interest) nextErrors.interest = 'Selecciona interes principal.'
    if (!form.consentAccepted) nextErrors.consentAccepted = 'Debes aceptar el consentimiento.'
    if (!turnstileToken) nextErrors.turnstileToken = 'Completa la verificacion anti-spam.'

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const submitLead = async (event) => {
    event.preventDefault()
    setSubmitState({ type: '', message: '' })

    if (!validateForm()) {
      return
    }

    setSending(true)
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.fullName.trim(),
          company: form.company.trim(),
          role: form.role.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          country: form.country,
          companySize: form.companySize,
          interest: form.interest,
          consentAccepted: form.consentAccepted,
          website: form.website,
          landingSlug,
          productCode,
          sessionId: sessionId || getStoredSessionId() || undefined,
          turnstileToken,
          ...utm
        })
      })

      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo registrar el lead.')
      }

      await trackEvent('form_submit_success', {
        leadId: payload?.leadId || null,
        interest: form.interest
      })

      setSubmitState({
        type: 'success',
        message: payload?.message || 'Solicitud recibida. Te contactaremos en menos de 24h habiles.'
      })
      setForm(initialForm)
      setTurnstileToken('')
      formStartedRef.current = false
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current)
      }
    } catch (error) {
      setSubmitState({ type: 'error', message: error?.message || 'No pudimos procesar tu solicitud en este momento.' })
    } finally {
      setSending(false)
    }
  }

  const onCtaClick = (placement) => {
    trackEvent('cta_click', { placement })
  }

  const onWhatsAppClick = () => {
    trackEvent('whatsapp_click', { channel: 'whatsapp' })
  }

  const sendTestCampaign = async () => {
    setTestCampaignState({ type: '', message: '' })
    setSendingTestCampaign(true)

    try {
      const response = await fetch('/api/integrations/brevo/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: `RentalBiz landing test ${new Date().toISOString()}`,
          subject: 'Prueba temporal desde landing RentalBiz',
          senderName: 'AppEmpreMail',
          type: 'classic',
          htmlContent: '<h1>Prueba Brevo</h1><p>Campana temporal lanzada desde la landing de RentalBiz.</p>',
          listIds: [2, 7]
        })
      })

      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo crear la campana de prueba.')
      }

      setTestCampaignState({
        type: 'success',
        message: `Campana creada en Brevo. ID: ${payload?.campaignId || 'n/d'}`
      })
    } catch (error) {
      setTestCampaignState({
        type: 'error',
        message: error?.message || 'Error creando la campana de prueba.'
      })
    } finally {
      setSendingTestCampaign(false)
    }
  }

  const sendSmtpTest = async () => {
    setSmtpTestState({ type: '', message: '' })
    setSendingSmtpTest(true)

    try {
      const response = await fetch('/api/integrations/smtp/test', {
        method: 'POST',
        credentials: 'include'
      })

      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo enviar la prueba SMTP.')
      }

      setSmtpTestState({
        type: 'success',
        message: `SMTP acepto el envio a ${payload?.recipientEmail || 'felipe.sotelo@live.com'}`
      })
    } catch (error) {
      setSmtpTestState({
        type: 'error',
        message: error?.message || 'Error enviando la prueba SMTP.'
      })
    } finally {
      setSendingSmtpTest(false)
    }
  }

  if (!isValidRoute) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <h1 className="text-3xl font-display text-white">Landing no disponible</h1>
          <p className="mt-4 text-slate-300">La ruta solicitada no corresponde a la categoria o producto de RentalBiz.</p>
          <Link to="/latam/soluciones/alquiler-inmuebles/rentalbiz" className="inline-flex mt-6 px-5 py-3 rounded-xl bg-cyan-500 text-slate-950 font-semibold">
            Ir a la landing principal LATAM
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#071116] text-slate-100">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-160px] left-[-120px] h-[520px] w-[520px] rounded-full bg-cyan-500/25 blur-3xl" />
        <div className="absolute top-[240px] right-[-160px] h-[520px] w-[520px] rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute bottom-[-220px] left-[20%] h-[480px] w-[480px] rounded-full bg-sky-600/20 blur-3xl" />
      </div>

      <LandingMainHeader className="bg-[#071116]/80" />

      <main>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="inline-flex px-3 py-1 rounded-full border border-cyan-300/30 text-cyan-200 text-xs uppercase tracking-wide">AppEmprendedor presenta RentalBiz</p>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display leading-tight text-white">
                Administra tus arriendos sin Excel, <span className="text-cyan-300">sin enredos y con control real.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-2xl">
                RentalBiz centraliza inmuebles, inquilinos, contratos, cobros y mantenimiento para que reduzcas mora y aumentes ocupacion.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={demoEntryUrl} onClick={() => onCtaClick('hero_demo')} className="px-6 py-3 rounded-xl border border-cyan-300/40 text-cyan-100 font-semibold">
                  Entrar a la demo
                </a>
                <a href="#form-lead" onClick={() => onCtaClick('hero_secondary')} className="px-6 py-3 rounded-xl border border-slate-600 text-slate-100 font-semibold">
                  Quiero cotizar
                </a>
                {showBrevoTestButton && (
                  <button
                    type="button"
                    onClick={sendTestCampaign}
                    disabled={sendingTestCampaign}
                    className="px-6 py-3 rounded-xl bg-amber-300 text-slate-950 font-semibold disabled:opacity-60"
                  >
                    {sendingTestCampaign ? 'Probando Brevo...' : 'Probar correo Brevo'}
                  </button>
                )}
                {showBrevoTestButton && (
                  <button
                    type="button"
                    onClick={sendSmtpTest}
                    disabled={sendingSmtpTest}
                    className="px-6 py-3 rounded-xl bg-emerald-300 text-slate-950 font-semibold disabled:opacity-60"
                  >
                    {sendingSmtpTest ? 'Probando SMTP...' : 'Probar SMTP'}
                  </button>
                )}
              </div>
              {showBrevoTestButton && testCampaignState.message && (
                <div className={`mt-4 max-w-xl rounded-xl px-4 py-3 text-sm ${testCampaignState.type === 'success' ? 'bg-emerald-500/10 border border-emerald-300/30 text-emerald-200' : 'bg-rose-500/10 border border-rose-300/30 text-rose-200'}`}>
                  {testCampaignState.message}
                </div>
              )}
              {showBrevoTestButton && smtpTestState.message && (
                <div className={`mt-4 max-w-xl rounded-xl px-4 py-3 text-sm ${smtpTestState.type === 'success' ? 'bg-emerald-500/10 border border-emerald-300/30 text-emerald-200' : 'bg-rose-500/10 border border-rose-300/30 text-rose-200'}`}>
                  {smtpTestState.message}
                </div>
              )}
            </div>
            <div className="rounded-3xl border border-white/10 bg-[#0f1f27]/80 p-6">
              <h2 className="text-xl text-white font-semibold">Problemas que resolvemos</h2>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Vacancias altas por falta de visibilidad comercial.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Mora y cartera desordenada sin seguimiento oportuno.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Contratos y renovaciones manejados en hojas de calculo.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Mantenimiento sin trazabilidad por inmueble.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#0d1a20]/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-3xl font-display text-white text-center">Como funciona en 3 pasos</h2>
            <div className="mt-10 grid md:grid-cols-3 gap-4">
              {[
                ['1', 'Organizamos tu portafolio', 'Estructuramos inmuebles, inquilinos, contratos y reglas de cobro.'],
                ['2', 'Activamos operacion diaria', 'Gestionas pagos, mora, mantenimiento y renovaciones desde un solo flujo.'],
                ['3', 'Escalas con indicadores', 'Mides ocupacion, ingresos y cartera para decisiones de crecimiento.']
              ].map(([step, title, text]) => (
                <article key={step} className="rounded-2xl border border-white/10 bg-[#122730]/80 p-5">
                  <p className="text-cyan-300 font-semibold">Paso {step}</p>
                  <h3 className="mt-2 text-lg text-white font-semibold">{title}</h3>
                  <p className="mt-2 text-sm text-slate-300">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section ref={featuresSectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-display text-white text-center">Funcionalidades clave de RentalBiz</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              [Building2, 'Propiedades / Inmuebles', 'Ficha unificada de propiedades residenciales y comerciales.'],
              [Users, 'Inquilinos', 'Historial, documentos y relacion contractual centralizada.'],
              [FileText, 'Contratos y Renovaciones', 'Control de vigencias, renovaciones y alertas de vencimiento.'],
              [Receipt, 'Cobros y Cartera', 'Facturacion, recibos, cartera y mora con seguimiento de recaudo.'],
              [Wrench, 'Mantenimiento', 'Solicitudes y tareas con trazabilidad por inmueble y responsable.'],
              [FileBarChart2, 'Reportes e Indicadores', 'Ocupacion, ingresos, mora y gastos para direccion y operaciones.']
            ].map(([Icon, title, text]) => (
              <article key={title} className="rounded-2xl border border-white/10 bg-[#10222a] p-5">
                <Icon className="w-5 h-5 text-cyan-300" />
                <h3 className="mt-3 text-white font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-slate-300">{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#0d1a20]/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-3xl font-display text-white text-center">Beneficios medibles</h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                [HandCoins, 'Menos mora', 'Seguimiento de vencimientos y cartera en tiempo real.'],
                [LayoutDashboard, 'Mejor ocupacion', 'Mas control comercial sobre unidades vacantes.'],
                [ChartColumn, 'Control de cartera', 'Tableros con estado de cobro por segmento y periodo.'],
                [ShieldCheck, 'Trazabilidad total', 'Contratos, pagos y mantenimiento con historial auditable.']
              ].map(([Icon, title, text]) => (
                <article key={title} className="rounded-2xl border border-cyan-300/20 bg-[#112733] p-5">
                  <Icon className="w-5 h-5 text-emerald-300" />
                  <h3 className="mt-3 text-white font-semibold">{title}</h3>
                  <p className="mt-2 text-sm text-slate-300">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="rounded-3xl border border-white/10 bg-[#10202a] p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h2 className="text-3xl font-display text-white">Prueba RentalBiz en una demo guiada</h2>
              <p className="mt-3 text-slate-300">Te mostramos un flujo real de inmuebles, contratos, cobros y mantenimiento para tu operacion LATAM.</p>
            </div>
            <div className="flex gap-3">
              <a href={whatsappLink} target="_blank" rel="noreferrer" onClick={onWhatsAppClick} className="px-6 py-3 rounded-xl border border-emerald-300/50 text-emerald-200 font-semibold inline-flex items-center gap-2">
                <PhoneCall className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#0d1a20]/80">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-3xl font-display text-white text-center">Preguntas frecuentes</h2>
            <div className="mt-8 space-y-3">
              {faqs.map((item) => (
                <details key={item.q} className="rounded-xl border border-white/10 bg-[#10232e] p-5">
                  <summary className="list-none cursor-pointer text-white font-semibold flex items-center justify-between gap-4">
                    {item.q}
                    <CircleHelp className="w-4 h-4 text-cyan-300" />
                  </summary>
                  <p className="mt-3 text-sm text-slate-300">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="form-lead" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-white/10 bg-[#10222a] p-8">
              <h2 className="text-3xl font-display text-white">Lleva tu operacion inmobiliaria a control profesional</h2>
              <p className="mt-4 text-slate-300">Completa el formulario y un consultor de AppEmprendedor te contacta para demo, cotizacion o consultoria.</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Enfoque en resultados financieros y operativos.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Implementacion estructurada para equipos LATAM.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />Respuesta en menos de 24h habiles.</li>
              </ul>
            </div>

            <form onSubmit={submitLead} className="rounded-3xl border border-white/10 bg-[#10222a] p-8 space-y-4" onFocus={startFormTracking}>
              <h3 className="text-xl font-semibold text-white">Solicita información de RentalBiz</h3>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <input
                    value={form.fullName}
                    onChange={(e) => updateField('fullName', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                    placeholder="Nombre completo"
                  />
                  {errors.fullName && <p className="text-xs text-rose-300 mt-1">{errors.fullName}</p>}
                </div>
                <div>
                  <input
                    value={form.company}
                    onChange={(e) => updateField('company', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                    placeholder="Empresa o Propietario"
                  />
                  {errors.company && <p className="text-xs text-rose-300 mt-1">{errors.company}</p>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <select
                    value={form.role}
                    onChange={(e) => updateField('role', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                  >
                    {roleOptions.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  {errors.role && <p className="text-xs text-rose-300 mt-1">{errors.role}</p>}
                </div>
                <div>
                  <input
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                    placeholder="Email"
                    type="email"
                  />
                  {errors.email && <p className="text-xs text-rose-300 mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <input
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                    placeholder="Telefono"
                  />
                  {errors.phone && <p className="text-xs text-rose-300 mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <select
                    value={form.country}
                    onChange={(e) => updateField('country', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                  >
                    {countries.map((countryName) => (
                      <option key={countryName} value={countryName}>{countryName}</option>
                    ))}
                  </select>
                  {errors.country && <p className="text-xs text-rose-300 mt-1">{errors.country}</p>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <select
                    value={form.companySize}
                    onChange={(e) => updateField('companySize', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                  >
                    {companySizes.map((size) => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                  {errors.companySize && <p className="text-xs text-rose-300 mt-1">{errors.companySize}</p>}
                </div>
                <div>
                  <select
                    value={form.interest}
                    onChange={(e) => updateField('interest', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0d1c24] border border-white/10 text-slate-100"
                  >
                    {interests.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errors.interest && <p className="text-xs text-rose-300 mt-1">{errors.interest}</p>}
                </div>
              </div>

              <label className="flex items-start gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={form.consentAccepted}
                  onChange={(e) => updateField('consentAccepted', e.target.checked)}
                  className="mt-1"
                />
                Acepto el tratamiento de datos para recibir informacion comercial y contacto de AppEmprendedor.
              </label>
              {errors.consentAccepted && <p className="text-xs text-rose-300">{errors.consentAccepted}</p>}

              <input
                value={form.website}
                onChange={(e) => updateField('website', e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
              />

              <div>
                <div ref={turnstileContainerRef} className="min-h-[65px]" />
                {errors.turnstileToken && <p className="text-xs text-rose-300 mt-1">{errors.turnstileToken}</p>}
                <p className="text-xs text-slate-400 mt-1">Protegido por Cloudflare Turnstile</p>
              </div>

              {submitState.message && (
                <div className={`rounded-xl px-4 py-3 text-sm ${submitState.type === 'success' ? 'bg-emerald-500/10 border border-emerald-300/30 text-emerald-200' : 'bg-rose-500/10 border border-rose-300/30 text-rose-200'}`}>
                  {submitState.message}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-emerald-400 text-slate-950 font-semibold disabled:opacity-60"
              >
                {sending ? 'Enviando...' : 'Solicitar información'}
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-sm text-slate-400">AppEmprendedor · RentalBiz para alquiler y administracion inmobiliaria en LATAM.</p>
          <div className="flex gap-4 text-sm">
            <Link to="/" className="text-slate-300 hover:text-cyan-300">Inicio</Link>
            <a href={demoEntryUrl} onClick={() => onCtaClick('footer_demo')} className="text-cyan-200 hover:text-cyan-100">Entrar a la demo</a>
            <a href="#form-lead" onClick={() => onCtaClick('footer_cta')} className="text-cyan-300 font-semibold">Solicitar información</a>
            <a href="/politica-de-privacidad" className="text-slate-300 hover:text-cyan-300">Politica de privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  )
}



